import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, Clock, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

/**
 * Single assignment-window editor for the hybrid schedule editor (KC-375).
 *
 * Windows are entered by date (weekday) with one uniform cohort-wide open/due
 * time (chosen at cohort creation). Week and assignment numbers are derived by the system, so this
 * form no longer carries the old 1–4 week / 1–2 assignment pickers or their
 * caps — it just returns a start/deadline window to the parent editor, which
 * owns persistence via the bulk schedule endpoint.
 */

interface WindowValue {
  start_date: string; // ISO date (YYYY-MM-DD)
  deadline: string; // ISO date (YYYY-MM-DD)
}

interface AssignmentFormProps {
  window?: WindowValue;
  onSave: (value: WindowValue) => void;
  onClose: () => void;
}

const windowFormSchema = z
  .object({
    start_date: z.string().min(1, "Start date is required"),
    deadline: z.string().min(1, "Deadline is required"),
  })
  .refine((v) => new Date(v.deadline) > new Date(v.start_date), {
    message: "Deadline must be after the start date",
    path: ["deadline"],
  });
type WindowFormValues = z.infer<typeof windowFormSchema>;

export function AssignmentForm({ window, onSave, onClose }: AssignmentFormProps) {
  const isEditMode = !!window;
  const form = useForm<WindowFormValues>({
    resolver: zodResolver(windowFormSchema),
    defaultValues: {
      start_date: window?.start_date ?? "",
      deadline: window?.deadline ?? "",
    },
  });
  const { control } = form;

  const onSubmit = form.handleSubmit((values) => {
    onSave({ start_date: values.start_date, deadline: values.deadline });
    onClose();
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900">
            {isEditMode ? "Edit assignment window" : "Add assignment window"}
          </h2>
          <button onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-5 p-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Start date <span className="text-red-500">*</span>
                    </FormLabel>
                    <div className="relative">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <FormControl>
                        <Input type="date" className="pl-10" {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="deadline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Deadline <span className="text-red-500">*</span>
                    </FormLabel>
                    <div className="relative">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <FormControl>
                        <Input type="date" className="pl-10" {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <p className="text-xs text-gray-500">
              <Clock className="mr-1 inline h-3 w-3" />
              Opens and closes at the cohort's schedule time on the selected dates. Week and
              assignment numbers are assigned automatically.
            </p>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-6 py-2 font-medium text-white transition-colors hover:bg-primary/90"
              >
                {isEditMode ? "Save window" : "Add window"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
